import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  CONNECTION_STATUS_LABELS,
  OAUTH_UNAVAILABLE_MESSAGE,
  PLATFORM_HANDLE_HINTS,
  PLATFORM_LABELS,
  PROOF_METHOD_LABELS,
  compactCount,
  connectedCountLabel,
  engagementUnknownHint,
  formatEngagementRate,
  reachSummary,
  type Connection,
  type ConnectionListResponse,
  type ConnectionPlatform,
  type Platform,
} from '@nexus/shared';

import { EMAIL_NOT_VERIFIED_MESSAGE } from '@nexus/shared';

import { useSession } from '../auth/SessionContext';
import { ApiError, apiGet, apiSend } from '../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ExternalLink,
  LoadErrorPage,
  LoadingPage,
  Num,
  PageHeader,
  Stat,
  StatGrid,
  statusLabel,
  StatusPill,
  statusTone,
  TextField,
  When,
  formatNumber,
} from '../ui';

/**
 * Connections: the accounts a creator has proven they own.
 *
 * ## Total reach is the first thing on the page
 *
 * The operator's instruction, and it is the right one: the single figure a
 * creator wants from this screen is how big their estate is, and until this
 * pass the only way to get it was to add five numbers up by hand. It is the
 * sum of followers across every live account on every platform, with the split
 * under it, and it counts the SAME accounts as the "n of 15" beside it —
 * `reachSummary` in `@nexus/shared` owns that arithmetic so the two figures
 * cannot come to disagree. A null follower count is not a zero: it adds
 * nothing and is reported as "n not read yet", because a headline that
 * silently dropped two of somebody's accounts is a headline they cannot check.
 *
 * ## The accounts run ACROSS the page, not down it
 *
 * THIS IS THE SHAPE OF THE PAGE AND IT IS THE WHOLE POINT OF THIS PASS. One
 * section per platform, always all three, and inside each one a ROW of account
 * columns rather than a stack of full-width cards. Fifteen accounts used to be
 * fifteen stacked cards — six or seven screens of scrolling to answer "what
 * have I connected" — and the same fifteen now fit in about two, because five
 * accounts on a platform is one row and not five rows.
 *
 * The row scrolls INSIDE ITS OWN CONTAINER when it is full, never taking the
 * page sideways with it (`shoot.mjs` fails any page that scrolls sideways at
 * 390px). Under 640px it stops being a row at all and becomes a compact list,
 * because five columns on a phone is five columns nobody can read.
 *
 * ## The card still shows HOW ownership was proven
 *
 * That is phase 2's "done means" and it has not moved: the proof method, when,
 * and the checks with what they saw. On a proven account they are folded into
 * the per-account Manage disclosure along with the three buttons, which is what
 * keeps a column short enough for five of them to sit side by side. On an
 * account that is NOT proven yet, the code and the Verify button stay in the
 * open — that is the one thing the creator came to this page to do.
 *
 * ## Every unknown number is an em dash with a reason
 *
 * `null` is NOT ZERO anywhere on this page. A YouTube account publishes no
 * following count and never will; an engagement rate needs at least three
 * posts that published likes or comments. Rendering either as "0" would state
 * something false about the creator's account, so they render as "—" and the
 * note under the figure says which of those it is.
 */
export function Connections(): React.JSX.Element {
  const [data, setData] = useState<ConnectionListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const { session } = useSession();
  /* The API refuses every connection MUTATION from an unconfirmed address
     (a bio-code check is a metered provider read, and a proven handle is
     taken out of circulation) with this same sentence; the list still
     loads, and disconnecting still works. Said up front so a creator does
     not type a handle into a form that cannot take it. */
  const emailVerified = session?.user.emailVerified !== false;

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await apiGet<ConnectionListResponse>('/connections'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your connections.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The OAuth callback sends the browser back here with the outcome in the
   * query string.
   *
   * It is captured on the FIRST render and then the query string is cleared, so
   * a reload does not re-announce a connection made ten minutes ago — and,
   * more importantly, clearing it does not take the message away with it. The
   * lazy initialiser is what makes those two things compatible: reading the
   * params inside the effect instead would erase the answer before the creator
   * had read it.
   */
  const [callback] = useState(() => {
    const result = params.get('result');
    return result
      ? { result, platform: params.get('platform'), detail: params.get('detail') }
      : null;
  });
  useEffect(() => {
    if (callback) setParams({}, { replace: true });
  }, [callback, setParams]);

  if (error && data === null) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="Connections"
        heading="Could not load your connections."
        onRetry={() => void load()}
      >
        <span>{error} Every account you have already proved is still proved.</span>
      </LoadErrorPage>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Creator" title="Connections" stats />;

  const closed = data.connections.filter((c) => c.status === 'REVOKED' || c.status === 'REJECTED');

  return (
    <>
      <PageHeader eyebrow="Creator" title="Connections" lead="Track every clip. Get paid faster." />

      {!emailVerified && (
        <Alert tone="waiting" title="Confirm your email first">
          {EMAIL_NOT_VERIFIED_MESSAGE}
        </Alert>
      )}

      {callback && (
        <Alert tone={callback.result === 'connected' ? 'positive' : 'critical'}>
          {callbackMessage(callback.result, callback.platform, callback.detail)}
        </Alert>
      )}

      <ReachStrip data={data} />

      {/* One section per platform, stacked, with the accounts running across
          each one. NOT the two-column masonry this page used to be: a row of
          five account columns needs the full content width, and half of it
          would put the horizontal scroller on at three accounts. */}
      {data.platforms.map((section) => (
        <PlatformSection
          key={section.platform}
          section={section}
          bioCode={data.bioCode}
          cooldownHours={section.metricsRefreshCooldownHours}
          onChanged={load}
        />
      ))}

      {closed.length > 0 && (
        <Card
          title="Closed connections"
          sub="Kept, not deleted: submissions and earnings made on these accounts still have to be explainable. They do not count against the five."
        >
          <ul className="rows">
            {closed.map((c) => (
              <li key={c.id}>
                <div className="row-head">
                  <span className="row-title">
                    {PLATFORM_LABELS[c.platform]} {c.handle}
                  </span>
                  <StatusPill
                    status={c.status}
                    domain="connection"
                    label={CONNECTION_STATUS_LABELS[c.status]}
                  />
                </div>
                {c.revokedReason && <span className="muted">{c.revokedReason}</span>}
                {c.revokedAt && (
                  <span className="muted">
                    Closed <When value={c.revokedAt} />.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function callbackMessage(result: string, platform: string | null, detail: string | null): string {
  const key = (platform ?? '').toUpperCase() as Platform;
  const name = PLATFORM_LABELS[key] ?? 'the platform';
  switch (result) {
    case 'connected':
      return `Connected to ${name}.`;
    case 'cancelled':
      return `You cancelled the ${name} sign-in. Nothing was connected.`;
    case 'refused':
      return detail === 'account_claimed_elsewhere'
        ? 'That account is already connected to another Nexus creator. If it is yours, ask the operator.'
        : detail === 'platform_limit_reached'
          ? `You already have the maximum number of ${name} accounts connected. Disconnect one first.`
          : 'That account could not be connected.';
    default:
      return `The ${name} sign-in did not finish${detail ? ` (${detail})` : ''}. Try again, or use the bio code.`;
  }
}

/* ------------------------------------------------------------------ */
/* Total reach                                                         */
/* ------------------------------------------------------------------ */

/**
 * The figure the operator asked for, at the top of the page.
 *
 * The big number is ABBREVIATED ("12.4K") because it is read at 2rem and a
 * seven-digit figure at that size is a ruler, not a number; the exact value is
 * in the `title` and in the per-platform split under it, which prints every
 * figure in full. Nothing on this strip is rounded away where it matters.
 */
function ReachStrip({ data }: { data: ConnectionListResponse }): React.JSX.Element {
  const reach = useMemo(() => reachSummary(data.platforms), [data.platforms]);

  return (
    <Card variant="feature" label="Total reach" tight>
      <div className="reach">
        <div className="reach-figure">
          <p className="reach-label">Total reach</p>
          <p className="reach-number" title={`${formatNumber(reach.totalFollowers)} followers`}>
            {compactCount(reach.totalFollowers)}
          </p>
          <p className="reach-split">
            {reach.byPlatform.map((row, index) => (
              <span key={row.platform}>
                {index > 0 && <span aria-hidden="true"> · </span>}
                {row.label} <Num value={row.followers} />
              </span>
            ))}
          </p>
        </div>

        <StatGrid dense>
          <Stat
            label="Accounts connected"
            value={`${reach.accountsConnected} of ${reach.accountLimit}`}
            note={
              reach.accountsWithoutFollowers > 0
                ? `${reach.accountsWithoutFollowers} of them have no follower count read yet, so they add nothing above.`
                : 'Five on each of the three platforms.'
            }
          />
          <Stat
            label="Last refreshed"
            value={<When value={reach.lastRefreshedAt} fallback="Never" />}
            note="The most recent profile read, across every account."
          />
        </StatGrid>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* One platform                                                        */
/* ------------------------------------------------------------------ */

/**
 * Does this rail actually have more in it than fits?
 *
 * Asked of the LAYOUT rather than guessed from the number of accounts: whether
 * five columns overflow depends on the window, the sidebar and the zoom, and a
 * component that guessed would put a "there is more" mark on a row that has
 * nothing more, or leave it off one that does.
 *
 * Chromium's scrollbars are overlay scrollbars — they fade out when nothing is
 * touching them — so at rest there is NOTHING on the screen saying a fourth
 * account can be reached, and at 1366px the fourth column is simply cut. This
 * is what turns that into a mark you can see without moving the mouse.
 */
function useOverflowing(): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = (): void => {
      /* One pixel of slack: sub-pixel layout rounds `scrollWidth` up on some
         zoom levels and a permanent one-pixel fade would be a lie. */
      setOverflowing(node.scrollWidth - node.clientWidth > 1);
    };
    measure();
    /* `ResizeObserver` is not in jsdom, where there is no layout to observe
       anyway: the first measurement is correct there (nothing overflows) and
       the screen renders exactly as it does today. */
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    /* BOTH BOXES. The wrap changes size when the window does; the rail INSIDE
       it changes when an account is connected or disconnected, which does not
       move the wrap at all — watching only one of them would leave the mark
       stale after exactly the action that changes it. */
    observer.observe(node);
    if (node.firstElementChild) observer.observe(node.firstElementChild);
    return () => observer.disconnect();
  }, []);

  return [ref, overflowing];
}

function PlatformSection({
  section,
  bioCode,
  cooldownHours,
  onChanged,
}: {
  section: ConnectionPlatform;
  bioCode: ConnectionListResponse['bioCode'];
  cooldownHours: number;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [adding, setAdding] = useState(false);
  const [rail, overflowing] = useOverflowing();

  return (
    <Card
      label={section.label}
      title={section.label}
      /* The engagement sentence, ONCE. It used to sit under every account's
         rate, which over fifteen accounts is fifteen copies of the same
         clause and about ninety pixels of the two screens this page has.
         NOT "against reach": the package divides by VIEWS where the feed
         publishes them and by FOLLOWERS where it does not, and which one
         produced a stored number is not recorded — so the sentence says what
         is true of both rather than naming a denominator it cannot know. */
      sub={
        /* Only where there is a rate to explain: a platform with nothing
           connected does not need a footnote about a column it has no rows
           in. */
        section.accounts.length > 0 ? 'Likes and comments on the last 10 posts.' : undefined
      }
      aside={
        <Badge tone={section.connectedCount > 0 ? 'positive' : 'neutral'}>
          {connectedCountLabel(section.connectedCount)}
        </Badge>
      }
      tight
    >
      {section.accounts.length === 0 ? (
        <div className="acct-rail-outer">
          <div className="acct-rail-wrap" ref={rail}>
            <ul className="acct-rail">
              <li className="acct acct-empty">
                <EmptyState title={`No ${section.label} account connected yet.`}>
                  Connect one and Nexus will read its public profile once to confirm it is yours.
                  Nothing is ever posted on your behalf.
                </EmptyState>
              </li>
              <AddTile section={section} open={adding} onToggle={() => setAdding((o) => !o)} />
            </ul>
          </div>
        </div>
      ) : (
        /* The scroller is the WRAP, not the page. `overflow-x: auto` with
           `min-width: 0` inside a grid child is what keeps five columns from
           widening the document — which `shoot.mjs` would fail at 390px. */
        <div
          className="acct-rail-outer"
          /* The mark that says there is more of this row than fits, painted by
             the stylesheet and set from the measured layout — never from a
             guess at how many columns a window holds. */
          data-overflowing={overflowing || undefined}
        >
          <div className="acct-rail-wrap" ref={rail}>
            <ul className="acct-rail">
              {section.accounts.map((account) => (
                <AccountColumn
                  key={account.id}
                  account={account}
                  removeNote={bioCode.removeAfterVerifying}
                  cooldownHours={cooldownHours}
                  onChanged={onChanged}
                />
              ))}
              <AddTile section={section} open={adding} onToggle={() => setAdding((o) => !o)} />
            </ul>
          </div>
          {overflowing && (
            <p className="acct-rail-more" aria-hidden="true">
              Scroll for more →
            </p>
          )}
        </div>
      )}

      {/* DISABLED AND EXPLAINED, never hidden. A creator at the limit who
          simply finds no button has no way to know whether that is the rule or
          a bug, and "disconnect one first" is the thing they can act on.

          UNDER THE RAIL and not inside it: at the limit the Add tile is the
          sixth thing in a row that shows four, so a sentence living on the tile
          would be a sentence off the side of the screen. A line rather than a
          full-width Alert — three tone-boxes shouting the same rule at somebody
          who has simply used all their slots is not news. */}
      {!section.canAddMore && (
        <p className="muted">
          {section.cannotAddReason ?? connectedCountLabel(section.connectedCount)}
        </p>
      )}

      {adding && section.canAddMore && (
        <ConnectForm
          section={section}
          bioCode={bioCode}
          onChanged={async () => {
            await onChanged();
          }}
        />
      )}
    </Card>
  );
}

/**
 * The last tile in the row: add another account here.
 *
 * Rendered AT THE LIMIT TOO, disabled, with the reason on it. A creator who
 * finds no button where a button was has no way to tell the rule from a bug,
 * and this page has made that argument about the OAuth buttons since phase 2.
 */
function AddTile({
  section,
  open,
  onToggle,
}: {
  section: ConnectionPlatform;
  open: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <li className="acct acct-add">
      <Button
        variant={section.accounts.length === 0 ? 'primary' : 'ghost'}
        onClick={onToggle}
        disabled={!section.canAddMore}
        aria-expanded={open}
        title={section.canAddMore ? undefined : (section.cannotAddReason ?? undefined)}
      >
        {open ? 'Cancel' : `Add ${section.label} account`}
      </Button>
      <span className="muted">
        {section.canAddMore
          ? `${section.remaining} of ${section.limit} left.`
          : connectedCountLabel(section.connectedCount)}
      </span>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* One connected account, as one column of the row                     */
/* ------------------------------------------------------------------ */

function AccountColumn({
  account,
  removeNote,
  cooldownHours,
  onChanged,
}: {
  account: Connection;
  removeNote: string;
  cooldownHours: number;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'positive' | 'critical'; text: string } | null>(
    null,
  );

  const verified = account.status === 'VERIFIED';
  const label = PLATFORM_LABELS[account.platform];
  const title = `${label} ${account.handle}`;

  async function act(
    what: 'verify' | 're-verify' | 'refresh-metrics' | 'disconnect',
  ): Promise<void> {
    setBusy(what);
    setMessage(null);
    try {
      if (what === 'disconnect') {
        await apiSend('DELETE', `/connections/${account.id}`);
      } else {
        await apiSend('POST', `/connections/${account.id}/${what}`);
        setMessage({
          tone: 'positive',
          text:
            what === 'verify'
              ? `Connected. ${removeNote}`
              : what === 'refresh-metrics'
                ? 'Read your profile again. The numbers below are current.'
                : 'Re-read your profile. Everything still lines up.',
        });
      }
      await onChanged();
    } catch (err) {
      setMessage({
        tone: 'critical',
        text: err instanceof ApiError ? err.message : 'Could not reach the API.',
      });
    } finally {
      setBusy(null);
    }
  }

  const engagement = formatEngagementRate(account.engagementRateBasisPoints);

  /* The four numbers the operator asked for, in the order they were asked for.
     Every one of them is nullable and a null is an em dash with a note, never
     a zero. In a column they are a 2x2 block rather than a row of tiles. */
  const figures: Array<{ label: string; value: React.ReactNode; note?: React.ReactNode }> = [
    {
      label: 'Followers',
      value: <Num value={account.followerCountAtConnect} />,
      /* TWO DIFFERENT NULLS, and the reach strip above already names one of
         them: an account nothing has read yet is "not read yet" there and must
         not be "the platform does not publish it" here — one screen giving two
         explanations for the same dash is how a creator decides the figure is
         broken. */
      ...(account.followerCountAtConnect === null
        ? {
            note:
              account.metricsRefreshedAt === null
                ? 'Not read yet. Press Refresh and we will read your profile once.'
                : 'Not published by the platform.',
          }
        : {}),
    },
    {
      label: 'Following',
      value: <Num value={account.followingCount} />,
      ...(account.followingCount === null
        ? {
            note:
              account.platform === 'YOUTUBE'
                ? 'YouTube does not publish this.'
                : 'Not read yet.',
          }
        : {}),
    },
    { label: 'Posts', value: <Num value={account.postCount} /> },
    {
      label: 'Engagement',
      value: engagement ?? <span className="muted">—</span>,
      /* ONLY when the figure is an em dash. A note under a number that is
         there says nothing the section heading has not already said; a note
         under a number that is MISSING is the difference between "—" reading
         as a fact about the platform and reading as a bug in Nexus. */
      ...(engagement === null
        ? { note: engagementUnknownHint(account.metricsSource, label) }
        : {}),
    },
  ];

  return (
    <li className="acct" data-connection={account.id} data-status={account.status}>
      <div className="acct-head">
        {/* The initial, not an avatar: Nexus never fetches a profile picture
            (it would be a third-party request from every creator's browser on
            every page load) and a letter in a disc is what tells five columns
            of the same platform apart at a glance. */}
        <span className="acct-avatar" aria-hidden="true">
          {initialOf(account.handle)}
        </span>
        <span className="acct-name">
          {account.profileUrl ? (
            <ExternalLink href={account.profileUrl}>{account.handle}</ExternalLink>
          ) : (
            account.handle
          )}
        </span>
        <Badge tone={statusTone(account.status, 'connection')}>
          {CONNECTION_STATUS_LABELS[account.status]}
        </Badge>
      </div>

      <dl className="acct-figures">
        {figures.map((figure) => (
          <div key={figure.label}>
            <dt>{figure.label}</dt>
            <dd>
              {figure.value}
              {figure.note && <span className="acct-note">{figure.note}</span>}
            </dd>
          </div>
        ))}
      </dl>

      <p className="acct-proof">
        Proven by {account.proofMethod ? PROOF_METHOD_LABELS[account.proofMethod] : 'nothing yet'}
        {account.verifiedAt && (
          <>
            , <When value={account.verifiedAt} />
          </>
        )}
        .
      </p>
      <p className="acct-proof">
        Numbers read <When value={account.metricsRefreshedAt} fallback="never" />.
      </p>

      {/* The code and the Verify button stay IN THE OPEN on an account that is
          not proven yet: it is the one thing the creator came to this page to
          do, and burying it in a disclosure would be burying the task. */}
      {account.bioCode && !verified && (
        <Alert tone="info" title="Put this code in your bio">
          <span>
            Add <code className="chip">{account.bioCode}</code> anywhere in your {label} bio, save
            it, then press Verify. We read your public profile once and never post anything.
          </span>
          {account.bioCodeExpiresAt && (
            <span className="muted">
              The code stops working on <When value={account.bioCodeExpiresAt} withTime />.
            </span>
          )}
        </Alert>
      )}

      {!verified && (
        <div className="actions">
          <Button variant="primary" small onClick={() => void act('verify')} disabled={busy !== null}>
            {busy === 'verify' ? 'Checking…' : 'Verify'}
          </Button>
          {/* "Disconnect" is the wrong word for an account that was never
              connected: the pending row is a code waiting in somebody's bio,
              and abandoning it is not the same act as unplugging a proven
              account. Same endpoint, honest label, and a question that matches
              what is actually about to happen. */}
          <ConfirmButton
            small
            label="Cancel this"
            title={`Stop connecting ${title}?`}
            question="The code stops working and this account goes away. You can start again at any time, with a new code."
            confirmLabel="Stop connecting it"
            disabled={busy !== null}
            onConfirm={() => void act('disconnect')}
          />
        </div>
      )}

      {/* THE COMPACT MENU, and the reason the row fits. Everything a proven
          account offers but rarely needs — the two reads, the disconnect, the
          bio-code reminder and the evidence — is folded into one disclosure,
          which is what keeps a column short enough for five of them to sit
          side by side and the whole page inside two screens. */}
      {verified && (
        <details className="acct-menu">
          <summary>Manage</summary>

          <div className="actions">
            {/* Refresh and Re-verify are two buttons on purpose. One asks "how
                is this account doing" and can only ever change a number; the
                other asks "is this still your account" and can fail. Folding
                them together would let a provider blip on a decorative figure
                produce a FAIL against a creator. */}
            <Button
              small
              onClick={() => void act('refresh-metrics')}
              disabled={busy !== null}
              title={`Reads your public profile and your last few posts. Once every ${cooldownHours} hours per account — the reads are metered, and Instagram's plan is 15,000 a month.`}
            >
              {busy === 'refresh-metrics' ? 'Reading…' : 'Refresh'}
            </Button>
            <Button small onClick={() => void act('re-verify')} disabled={busy !== null}>
              {busy === 're-verify' ? 'Re-reading…' : 'Re-verify'}
            </Button>
            <ConfirmButton
              small
              label="Disconnect"
              title={`Disconnect ${title}?`}
              question="Clips you have already submitted from this account keep their earnings. You will have to prove the account again before you can submit from it, and the proof starts from scratch. The slot goes back on this platform."
              confirmLabel="Disconnect it"
              disabled={busy !== null}
              onConfirm={() => void act('disconnect')}
            />
          </div>

          {account.bioCode && account.proofMethod === 'BIO_CODE' && (
            <p className="muted">
              {/* `removeNote` is already a whole sentence from the API, and
                  this used to open with its own version of it, so the card
                  said it twice in a row. The code itself is the only thing the
                  API's sentence does not carry. */}
              {removeNote} Yours was <code className="chip quiet">{account.bioCode}</code>.
            </p>
          )}

          {account.checks.length > 0 && <Evidence account={account} />}
        </details>
      )}

      {message && <Alert tone={message.tone}>{message.text}</Alert>}
    </li>
  );
}

/** The first letter of a handle, for the disc beside it. */
function initialOf(handle: string): string {
  const letter = handle.replace(/^@/, '').trim().charAt(0);
  return letter ? letter.toUpperCase() : '?';
}

/** The named checks that proved this account, with what each one saw. */
function Evidence({ account }: { account: Connection }): React.JSX.Element {
  return (
    <div className="acct-evidence">
      <p className="acct-proof">
        What proved it ({account.checks.length} check{account.checks.length === 1 ? '' : 's'})
      </p>
      <ul className="checklist">
        {account.checks.map((check) => (
          <li
            key={check.id}
            data-result={check.result}
            data-state={
              check.result === 'PASS' ? 'pass' : check.result === 'FAIL' ? 'fail' : 'exception'
            }
          >
            <span className="mark" aria-hidden="true">
              {check.result === 'PASS' ? '✓' : check.result === 'FAIL' ? '✕' : '?'}
            </span>
            <span>
              <span className="check-label">{statusLabel(check.checkType)}</span>{' '}
              <span className="muted">
                <When value={check.checkedAt} withTime />
              </span>
              {check.note && <div className="check-note">{check.note}</div>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Adding one to this platform                                         */
/* ------------------------------------------------------------------ */

function ConnectForm({
  section,
  bioCode,
  onChanged,
}: {
  section: ConnectionPlatform;
  bioCode: ConnectionListResponse['bioCode'];
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [handle, setHandle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ code: string; instructions: string[] } | null>(null);

  const platform = section.platform;
  const provider = section.oauth;

  async function startBioCode(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setIssued(null);
    try {
      const body = await apiSend<{ code: string; instructions: string[] }>(
        'POST',
        '/connections/bio-code',
        { platform, handle },
      );
      setIssued(body);
      setHandle('');
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API.');
    } finally {
      setBusy(false);
    }
  }

  async function startOAuth(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await apiSend<{ authorizeUrl: string }>(
        'POST',
        `/oauth/${platform.toLowerCase()}/start`,
      );
      window.location.assign(body.authorizeUrl);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the API.');
      setBusy(false);
    }
  }

  return (
    <div className="sub-section" data-add-account={platform}>
      <hr className="divider" />
      <h3>Connect another {section.label} account</h3>
      <p className="muted">
        Two ways to prove an account is yours. Either one is enough, and neither lets Nexus post.
      </p>

      <div className="split even">
        <div className="sub-section">
          <h4>Sign in with {section.label}</h4>
          <p className="muted">{provider.explanation}</p>
          <div className="actions">
            <Button
              variant="primary"
              onClick={() => void startOAuth()}
              disabled={busy || !provider.available}
              /* Disabled and EXPLAINED, never hidden: platform app review takes
                 weeks, and a creator staring at a missing button has no way to
                 know the bio code below is the intended path today. */
              title={
                provider.available
                  ? undefined
                  : (provider.unavailableReason ?? OAUTH_UNAVAILABLE_MESSAGE)
              }
            >
              Sign in with {section.label}
            </Button>
            {!provider.available && (
              <span className="muted">{provider.unavailableReason ?? OAUTH_UNAVAILABLE_MESSAGE}</span>
            )}
          </div>
        </div>

        <div className="sub-section">
          <h4>Or use a bio code</h4>
          <p className="muted">
            We give you a short code, you put it in your bio, we read your public profile once. The
            code lasts {bioCode.ttlHours} hours and you can check up to {bioCode.attemptsPerHour}{' '}
            times an hour. {bioCode.removeAfterVerifying}
          </p>

          <form onSubmit={startBioCode} className="form sub-form">
            <TextField
              label={`${section.label} handle`}
              type="text"
              name="handle"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@yourhandle"
              required
              hint={PLATFORM_HANDLE_HINTS[platform]}
              {...(error ? { 'aria-invalid': true } : {})}
            />
            <Button type="submit" variant={provider.available ? 'ghost' : 'primary'} disabled={busy}>
              {busy ? 'Working…' : 'Get a code'}
            </Button>
          </form>
        </div>
      </div>

      {issued && (
        <Alert tone="positive" title="Your code is ready">
          <span>
            <code className="chip">{issued.code}</code>
          </span>
          <ol>
            {issued.instructions.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
        </Alert>
      )}

      {error && <Alert tone="critical">{error}</Alert>}
    </div>
  );
}
